import { Tooltip } from "@bryntum/scheduler";
import { BryntumScheduler } from "@bryntum/scheduler-react";
import handleEventClick from "components/bryntum/handleEventClick";
import clickOnDateHeader from "components/bryntum/listeners/clickOnDateHeader";
import dragToReschedule from "components/bryntum/listeners/dragToReschedule";
import dropToReschedule from "components/bryntum/listeners/dropToReschedule";
import openModal from "components/bryntum/listeners/openModal";
import resizeToReschedule from "components/bryntum/listeners/resizeToReschedule";
import filterHeaderRenderer from "components/common/Scheduler/SchedulerRenderers/renderers/filterHeaderRenderer";
import lineRenderer from "components/common/Scheduler/SchedulerRenderers/renderers/lineRenderer";
import renderToString from "components/common/Scheduler/SchedulerRenderers/renderers/renderToString";
import LineTooltip from "components/common/Scheduler/tooltips/LineTooltip";
import ShiftInstanceTooltip from "components/common/Scheduler/tooltips/ShiftInstanceTooltip";
import UnavailabilityTooltip from "components/common/Scheduler/tooltips/UnavailabilityTooltip";
import BLOCK_HEIGHT from "components/pages/schedule/helpers/blockHeight";
import GapFiller from "components/pages/schedule/helpers/GapFiller";
import { defaultViewPresetId, getTickWidth, presetId, viewPresets } from "config/zoomConfig";
import UIStateContext from "contexts/UIStateContext";
import WorkOrderSearchContext from "contexts/WorkOrderSearchContext";
import ApplicationContext from "domain/ApplicationContext";
import Bryntum from "domain/Bryntum";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import useCloseGap from "hooks/apollo/blocks/useCloseGap";
import useRescheduleBlocks from "hooks/apollo/blocks/useRescheduleBlocks";
import useGetLines from "hooks/apollo/line/useGetLines";
import useRefetchSchedule from "hooks/apollo/schedule/useRefetchSchedule";
import useTrackUsage from "hooks/apollo/usageData/useTrackUsage";
import useFetchSchedulerData from "hooks/apollo/useFetchSchedulerData";
import useFullyScheduleWorkOrder from "hooks/apollo/workBlock/useFullyScheduleWorkOrder";
import useGetWorkOrders from "hooks/apollo/workOrder/useGetWorkOrders";
import useEventFeatures from "hooks/schedule/useEventFeatures";
import useBlockCreateModal from "hooks/state/useBlockCreateModal";
import useDowntimeEditModal from "hooks/state/useDowntimeEditModal";
import useDowntimeShowModal from "hooks/state/useDowntimeShowModal";
import useHighlightWorkBlock from "hooks/state/useHighlightWorkBlock";
import useNotesPopUp from "hooks/state/useNotesPopUp";
import useSchedulerState from "hooks/state/useSchedulerState";
import useWorkBlockEditModal from "hooks/state/useWorkBlockEditModal";
import useWorkBlockShowModal from "hooks/state/useWorkBlockShowModal";
import useBryntumScheduler from "hooks/useBryntumScheduler";
import assign from "lodash/assign";
import Authorization from "policies/Authorization";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import DateTime from "utils/DateTime";

const RESIZE_DELAY = 500;

// Reference:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function handleMultiSelectMode({ selection }) {
  if (selection.length > 1) {
    this.currentElement.classList.add("multi-select-mode");
  } else {
    this.currentElement.classList.remove("multi-select-mode");
  }
}

const SchedulerContainer = styled.div`
  grid-column: 1 / 2;
  grid-row: 2;
  overflow: hidden;
  padding: 10px 0 0 0;
`;

export default function BryntumSchedulerContainer() {
  const schedulerRef = React.createRef();
  const resizeTimer = useRef(null);

  const policy = Authorization.usePolicy();
  const applicationContext = useApplicationContext();
  const onTrackUsage = useTrackUsage();
  const { eventFeatures } = useEventFeatures();
  const { setBryntumScheduler, getBryntumScheduler } = useBryntumScheduler();
  const { getState: getExistingSearchState } = useContext(WorkOrderSearchContext);
  const { highlightWorkBlock } = useHighlightWorkBlock();
  const {
    state: { zoomLevel, extendedView, visibleTimeRange, dataTimeRange },
    changeDataTimeRange,
    changeVisibleTimeRange,
    zoomIn,
  } = useSchedulerState();
  const {
    state: { selectedWorkBlock, sidebarOpen },
  } = useContext(UIStateContext);

  const refetchSchedule = useRefetchSchedule();
  const rescheduleBlocks = useRescheduleBlocks();
  const fullyScheduleWorkOrder = useFullyScheduleWorkOrder();
  const { handleCloseGap } = useCloseGap();
  const { lines } = useGetLines({ activeOnly: true });
  const { fetch: refetchWorkOrders } = useGetWorkOrders();

  const {
    blocks: { workBlocks, downtimeBlocks },
    shiftInstances,
    unavailabilities,
  } = useFetchSchedulerData();

  const { settings } = applicationContext;
  // This is generally bad practice! useMemo is supposed to be called with dependencies.
  // It is being done here to keep the number of global event listeners from growing on each re-render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  const gapFiller = useMemo(() => new GapFiller({ settings }), []);
  const readonly = ApplicationContext.isReadonly(applicationContext);
  const canUpdate = !readonly && policy.canUpdate(Authorization.EntityTypes.WorkBlock);

  const { open: onWorkBlockShowModal } = useWorkBlockShowModal();
  const { open: onDowntimeShowModal } = useDowntimeShowModal();
  const { open: handleOpenNotesPopUp } = useNotesPopUp();
  const { open: onOpenEditWorkBlock } = useWorkBlockEditModal();
  const { open: onOpenCreateBlock } = useBlockCreateModal();
  const { open: onOpenEditDowntime } = useDowntimeEditModal();

  const onOpenModal = openModal({ onOpenCreateBlock, onOpenEditDowntime, onOpenEditWorkBlock });
  const onOpenShowModal = openModal({
    onOpenEditDowntime: onDowntimeShowModal,
    onOpenEditWorkBlock: onWorkBlockShowModal,
  });

  const [columns] = useState([
    {
      cls: "line-header-cell",
      filterable: ({ value, record }) => {
        const terms = value
          .split(",")
          .map((term) => escapeRegExp(term.trim()))
          .join("|");
        const regex = RegExp(`.*(${terms}).*`, "i");

        return record.lineType.match(regex) || record.name.match(regex);
      },
      htmlEncode: false,
      htmlEncodeHeaderText: false,
      renderer: lineRenderer(),
      showImage: false,
      text: filterHeaderRenderer(),
      tooltip: {
        align: "l-r",
        html: "To filter for multiple lines and/or line types, use a comma-separated search (e.g. Line 1, Line 2, Type Mix)",
      },
      width: 130,
    },
  ]);
  const onInfiniteScroll = (props) => {
    refetchSchedule(changeDataTimeRange({ dataTimeRange: { ...props.new } }));
  };

  const handleFullyScheduleWorkOrder = ({ eventRecord: { data: blockData } }) => {
    const { block: workBlock } = blockData;

    fullyScheduleWorkOrder(workBlock);
  };

  const closeGap = ({ date, resourceRecord: { data } }) => {
    handleCloseGap({ date, lineId: data.id });
  };

  const handleRefetchWorkOrders = () => {
    const existingState = getExistingSearchState();
    const {
      filters: { filterByScheduleView },
    } = existingState;

    if (filterByScheduleView) refetchWorkOrders({ ...existingState });
  };

  const visibleDateRangeChanged = (props) => {
    changeVisibleTimeRange({ visibleTimeRange: props.new });
    const existingState = getExistingSearchState();
    const {
      filters: { filterByScheduleView },
    } = existingState;

    if (filterByScheduleView) refetchWorkOrders({ ...existingState, visibleTimeRange: props.new });
  };

  const resizePresets = () => {
    const scheduler = getBryntumScheduler();

    if (!scheduler) return;

    viewPresets.forEach((preset) => {
      const newPreset = { ...preset, tickWidth: getTickWidth(preset.ticks, sidebarOpen) };
      scheduler.presets.add(newPreset);
    });
    const newStartDate = extendedView ? new Date(visibleTimeRange.startDate) : dataTimeRange.startDate;

    scheduler.zoomTo({
      endDate: DateTime.addDuration(newStartDate, 14, "days"),
      preset: presetId(zoomLevel),
      startDate: newStartDate,
      visibleDate: { block: "start", date: new Date(visibleTimeRange.startDate) },
      zoomLevel,
    });
  };

  const handleTimedWindowResize = () => {
    clearTimeout(resizeTimer.current);
    resizeTimer.current = setTimeout(() => {
      resizePresets();
    }, RESIZE_DELAY);
  };

  const lineTooltipRenderer = ({ record }) => LineTooltip({ record });

  const [config] = useState({
    autoCreate: false,
    barMargin: 2,
    // Reference:
    // https://bryntum.com/products/scheduler/docs/api/Scheduler/view/mixin/TimelineScroll#config-infiniteScroll
    bufferCoef: 3,
    bufferThreshold: 0.1, // Prevents events from stacking and making the row higher (we bump so no need)
    cellEditFeature: false,
    cellMenuFeature: {
      items: {
        removeRow: false,
      },
    },
    cellTooltipFeature: {
      tooltipRenderer: lineTooltipRenderer,
    },
    contextMenuFeature: false,
    endDate: visibleTimeRange.endDate,
    eventLayout: "none",
    filterBarFeature: true,
    headerMenuFeature: false,
    infiniteScroll: extendedView,
    multiEventSelect: true,
    regionResizeFeature: {
      showSplitterButtons: false,
    },
    resourceTimeRangesFeature: true,
    rowCopyPasteFeature: false,
    rowHeight: BLOCK_HEIGHT + 2,
    scheduleMenuFeature: {
      processItems: ({ items }) => {
        if (!canUpdate) return false;

        return assign(items, {
          addEvent: false,
          closeGap: {
            onItem: closeGap,
            text: "Close gap",
          },
        });
      },
    },
    startDate: visibleTimeRange.startDate,
    stripeFeature: true,
    timeRangesFeature: {
      headerRenderer({ timeRange }) {
        return timeRange.name;
      },
      showCurrentTimeLine: true,
      showHeaderElements: true,
      tooltipTemplate: ShiftInstanceTooltip,
    },
    transitionDuration: 0,
    useInitialAnimation: false,
    ...eventFeatures(gapFiller),
    callOnFunctions: true,
    columns,
    displayDateFormat: "MMM D, YYYY hh:mm A",
    emptyText: "No results found",
    enableDeleteKey: false,
    events: [],
    focusCls: "event-focused",
    maxZoomLevel: viewPresets.length - 1,
    minZoomLevel: 0,
    nonWorkingTime: true,
    onDateRangeChange: onInfiniteScroll,
    onEventClick: handleEventClick(handleFullyScheduleWorkOrder, handleOpenNotesPopUp, readonly),
    readOnly: !canUpdate,
    resources: lines,
    resourceTimeRanges: [],
    scheduleTooltipFeature: false,
    scrollManager: {
      startScrollDelay: 0,
    },
    timeAxisHeaderMenuFeature: false, // custom focused class to disable default focused styling
    timeRanges: [],
    viewPreset: zoomLevel ? presetId(zoomLevel) : defaultViewPresetId,
    zoomOnMouseWheel: false,
    zoomOnTimeAxisDoubleClick: false,
  });

  const [listeners] = useState({
    afterEventDrop: dropToReschedule({ gapFiller, onRescheduleBlocks: rescheduleBlocks, onTrackUsage }),
    beforeEventEdit: readonly ? onOpenShowModal : onOpenModal,
    eventDrag: dragToReschedule({ gapFiller }),
    eventResizeEnd: resizeToReschedule(rescheduleBlocks),
    eventSelectionChange: handleMultiSelectMode,
    timeAxisHeaderClick: clickOnDateHeader(zoomIn),
  });

  // A lot of these "depend" on functions that get recreated on render which cause infinite loops if
  // they are in the dependencies
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    window.addEventListener("resize", handleTimedWindowResize);
  }, []); // Empty dependency array ensures this effect runs once on mount

  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    resizePresets();
  }, [sidebarOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (schedulerRef.current.instance.viewPreset.id === presetId(zoomLevel)) return;

    schedulerRef.current.instance.zoomTo({
      preset: presetId(zoomLevel),
      visibleDate: { block: "start", date: visibleTimeRange.startDate },
      zoomLevel,
    });
  }, [schedulerRef.current, zoomLevel]);

  // The config is captured once, so the instance keeps the infiniteScroll it was built with. While that is on
  // Bryntum ignores the startDate and endDate the effects below pass to zoomTo, so it has to be set first.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    schedulerRef.current.instance.infiniteScroll = extendedView;
  }, [extendedView]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (extendedView) {
      schedulerRef.current.instance.scrollToDate(new Date(visibleTimeRange.startDate), { block: "start" }).then(() => {
        highlightWorkBlock();
      });
    }
  }, [extendedView, schedulerRef.current, visibleTimeRange]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (!extendedView) {
      const newStartDate = new Date(visibleTimeRange.startDate);

      schedulerRef.current.instance.zoomTo({
        endDate: DateTime.addDuration(newStartDate, 14, "days"),
        preset: presetId(zoomLevel),
        startDate: newStartDate,
        zoomLevel,
      });

      schedulerRef.current.instance.scrollToDate(new Date(visibleTimeRange.startDate), { block: "start" });
      schedulerRef.current.instance.timeAxis.reconfigure({});

      highlightWorkBlock();
    }
  }, [dataTimeRange, extendedView, schedulerRef.current]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    highlightWorkBlock();
  }, [selectedWorkBlock]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    setBryntumScheduler(schedulerRef.current.instance);
    gapFiller.setSchedule(schedulerRef.current.instance);

    // This listener has to be added here and not in the listeners array so we can add a buffer to it
    // If we add the buffer to the listeners array, it will be added to all of the listeners and that makes
    // the default Bryntum Edit and Create popups show up.
    // This method is recommended by Bryntum for things needing buffers
    schedulerRef.current.instance.addListener({
      buffer: 300,
      thisObj: this,
      visibleDateRangeChange: visibleDateRangeChanged,
    });

    schedulerRef.current.instance.resourceStore.on("filter", handleRefetchWorkOrders);

    schedulerRef.current.instance.rangeTooltip = new Tooltip({
      allowOver: true,
      forSelector: ".b-sch-resourcetimerange",
      getHtml: ({ activeTarget }) =>
        renderToString(<UnavailabilityTooltip activeTarget={activeTarget} policy={policy} />),
      hoverDelay: 100,
      showOnHover: true,
    });
  }, [schedulerRef.current, gapFiller]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    const workBlockEvents = Bryntum.buildWorkBlockEvents(workBlocks);
    const builtShiftTimeRanges = Bryntum.buildShiftTimeRanges(shiftInstances);
    const downtimeBlockEvents = Bryntum.buildDowntimeBlockEvents(downtimeBlocks);
    const builtUnavailabilityTimeRanges = Bryntum.buildUnavailabilityTimeRanges(unavailabilities);

    schedulerRef.current.instance.events = workBlockEvents.concat(downtimeBlockEvents);
    schedulerRef.current.instance.timeRanges = builtShiftTimeRanges;
    schedulerRef.current.instance.resourceTimeRanges = builtUnavailabilityTimeRanges;
  }, [shiftInstances, downtimeBlocks, workBlocks, unavailabilities]);

  return (
    <SchedulerContainer id="bryntum-scheduler">
      <BryntumScheduler ref={schedulerRef} {...config} listeners={listeners} presets={viewPresets} />
    </SchedulerContainer>
  );
}
